import { TaskKanbanFilterType } from '../enums';

export interface StoredTaskKanbanFilter {
    scope: TaskKanbanFilterType;
    team_ids: ID[];
    client_ids: ID[];
}

const taskKanbanFilterTypes = [
    TaskKanbanFilterType.ALL,
    TaskKanbanFilterType.MY_TASKS,
    TaskKanbanFilterType.MY_TEAMS,
];

const parseScope = (value: unknown): TaskKanbanFilterType | null => {
    const scope = taskKanbanFilterTypes.find((filterType) => filterType === value);

    return scope ?? null;
};

// Only IDs are persisted, so no client or team object ever reaches session storage
const parseIdList = (value: unknown): ID[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((id): id is ID => typeof id === 'string' && id.length > 0);
};

/**
 * Reads the stored kanban filter, accepting both the current JSON value and the legacy plain scope string
 */
export const parseStoredTaskKanbanFilter = (rawValue: string | null): StoredTaskKanbanFilter | null => {
    if (!rawValue) {
        return null;
    }

    // Legacy value is the bare scope string
    const legacyScope = parseScope(rawValue);
    if (legacyScope !== null) {
        return { scope: legacyScope, team_ids: [], client_ids: [] };
    }

    try {
        const parsedValue = JSON.parse(rawValue);
        const scope = parseScope(parsedValue?.scope);
        if (scope === null) {
            return null;
        }

        return {
            scope,
            team_ids: parseIdList(parsedValue?.team_ids),
            client_ids: parseIdList(parsedValue?.client_ids),
        };
    } catch (e) {
        return null;
    }
};

/**
 * Serializes the kanban filter for session storage
 */
export const serializeTaskKanbanFilter = (storedFilter: StoredTaskKanbanFilter): string => {
    return JSON.stringify(storedFilter);
};
